import { useEffect, useRef, useState } from "react";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../auth/auth";

const isDeepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== typeof obj2 || obj1 === null || obj2 === null) return false;

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    return obj1.every((val, index) => isDeepEqual(val, obj2[index]));
  }

  const keys1 = Object.keys(obj1).sort();
  const keys2 = Object.keys(obj2).sort();
  if (keys1.length !== keys2.length || !keys1.every((k, i) => k === keys2[i])) return false;
  return keys1.every(key => isDeepEqual(obj1[key], obj2[key]));
};

export function useCanvasFirebase(slideId, isEditable = false) {
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Add these with your other useRef hooks
const lastSyncRef = useRef(null);
const localUpdateRef = useRef(false);
const hasLoadedRef = useRef(false);

// 1. Listen for remote changes
useEffect(() => {
  if (!slideId) return;
  const slideRef = doc(db, "slides", slideId);

  const unsubscribe = onSnapshot(slideRef, (snap) => {
    if (snap.exists()) {
      // If we are currently saving, ignore the snapshot to prevent an echo loop
      if (localUpdateRef.current) {
        localUpdateRef.current = false;
        return;
      }

      const remoteData = snap.data().content || [];
      
      setElements((currentElements) => {
        if (isDeepEqual(currentElements, remoteData)) return currentElements;

        // Update the 'lastSyncRef' so the Auto-Save knows this came from the cloud
        lastSyncRef.current = JSON.stringify(remoteData); 
        console.log("FIREBASE: GET => Remote Sync: Data updated from cloud.");
        return remoteData;
      });
    }
    setLoading(false);
    hasLoadedRef.current = true;
  });

  return () => unsubscribe();
}, [slideId]);

useEffect(() => {
  if (!isEditable || !hasLoadedRef.current || !slideId) return;

  const currentDataString = JSON.stringify(elements);

  // CRITICAL: If current data matches what we just got from the cloud, ABORT SAVE
  if (lastSyncRef.current === currentDataString) {
    return; 
  }

  const performSave = async () => {
    try {
      localUpdateRef.current = true; // Lock the listener
      lastSyncRef.current = currentDataString; // Update our "known" state
      console.log('FIREBASE: POST => ',  elements);
      await updateDoc(doc(db, "slides", slideId), {
        content: elements,
        updatedAt: Date.now(),
      });
      console.log("Auto-save successful");
    } catch (err) {
      console.error("Auto-save failed:", err);
      localUpdateRef.current = false;
    }
  };

  // Add a slight delay (debounce) to prevent excessive writes
  const timeoutId = setTimeout(performSave, 1000);
  return () => clearTimeout(timeoutId);

}, [elements, isEditable, slideId]);

  return { 
    elements, 
    setElements, 
    loading 
  };
}